import { LitElement, html, css, type PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { dispatch } from '../../utils/event-helpers';
import { generateUid } from '../../utils/uid';
import { NavbarController } from './navbar.controller';

export type NavbarType = '3-col' | 'floating-2-col';
type NavItemContract = {
  type: NavbarType;
  inNavbar: boolean;
  mobile: boolean;
  navbarHost: HTMLElement;
};

/**
 * A persistent full-width site navigation bar. Composes a brand logo,
 * `<asc-nav-item>` children, and an `<asc-nav-actions>` area into a responsive
 * shell. On desktop all content sits inline; on mobile a hamburger-toggled panel
 * reveals the nav items and actions below the bar.
 *
 * @element asc-navbar
 *
 * @fires asc-open  - Fired when the mobile panel opens due to user interaction. No detail.
 * @fires asc-close - Fired when the mobile panel closes due to user interaction. No detail.
 *
 * @slot logo    - Required. Brand logo (logomark + wordmark). Wrap in `<a href="/">` for SEO.
 * @slot         - One or more `<asc-nav-item>` elements.
 * @slot actions - Auth-state controls (Log In / Sign Up buttons, or an avatar when authenticated). Accepts any content — typically `<asc-button>` or `<asc-avatar>` elements. Right-aligned on desktop as a flex row; stacked full-width at the bottom of the mobile panel.
 *
 * @csspart base         - Root `<header>` landmark.
 * @csspart bar          - Full-width bar background.
 * @csspart container    - Max-width + padding inner container.
 * @csspart logo-area    - Wrapper for the logo slot.
 * @csspart nav-area     - `<nav>` holding items and actions; becomes mobile panel.
 * @csspart nav-items    - Wrapper for the default slot.
 * @csspart actions-area - Wrapper for the actions slot.
 * @csspart hamburger    - Internal mobile toggle button.
 * @csspart backdrop     - Semi-transparent overlay behind mobile panel.
 *
 * @cssprop --asc-navbar-bg                        - Bar background. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-navbar-max-width                 - Content area max-width. Default: `1280px`
 * @cssprop --asc-navbar-padding-x                 - Desktop horizontal padding. Default: `var(--asc-container-padding-desktop)`
 * @cssprop --asc-navbar-mobile-padding-x          - Mobile start-side padding. Default: `var(--asc-container-padding-mobile)`
 * @cssprop --asc-navbar-mobile-padding-end        - Mobile end-side padding. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-navbar-logo-nav-gap              - Gap between the logo slot and the nav-items group (desktop). Default: `var(--asc-spacing-2xl)`
 * @cssprop --asc-navbar-bar-gap                   - Gap between the nav-items group and the actions-area (desktop). Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-navbar-nav-items-gap             - Gap between individual `<asc-nav-item>` elements in the desktop bar. Default: `var(--asc-spacing-xxs)`
 * @cssprop --asc-navbar-height-desktop            - Bar height on desktop (3-col). Default: `80px`
 * @cssprop --asc-navbar-height-desktop-floating   - Bar height on desktop (floating-2-col). Default: `76px`
 * @cssprop --asc-navbar-height-mobile             - Bar height on mobile (3-col). Default: `72px`
 * @cssprop --asc-navbar-height-mobile-floating    - Bar height on mobile (floating-2-col). Default: `64px`
 * @cssprop --asc-navbar-panel-bg                  - Mobile panel background. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-navbar-panel-shadow              - Mobile panel shadow. Default: multi-layer shadow-lg
 * @cssprop --asc-navbar-panel-nav-padding-y       - Panel nav-items vertical padding. Default: `var(--asc-spacing-2xl)`
 * @cssprop --asc-navbar-panel-nav-gap             - Panel nav-items gap. Default: `var(--asc-spacing-xxs)`
 * @cssprop --asc-navbar-panel-border-color        - Panel nav/actions divider colour. Default: `var(--asc-border-secondary)`
 * @cssprop --asc-navbar-panel-actions-padding-y   - Panel actions vertical padding. Default: `var(--asc-spacing-3xl)`
 * @cssprop --asc-navbar-panel-actions-padding-x   - Panel actions horizontal padding. Default: `var(--asc-container-padding-mobile)`
 * @cssprop --asc-navbar-panel-transition          - Panel open/close transition duration. Default: `300ms`
 * @cssprop --asc-navbar-backdrop-color            - Backdrop overlay colour. Default: `var(--asc-alpha-black-50)`
 * @cssprop --asc-navbar-hamburger-padding         - Hamburger button padding. Default: `var(--asc-spacing-md)`
 * @cssprop --asc-navbar-hamburger-radius          - Hamburger button border-radius. Default: `var(--asc-radius-md)`
 * @cssprop --asc-navbar-hamburger-icon-size       - Hamburger icon size. Default: `24px`
 * @cssprop --asc-navbar-hamburger-bg-hover        - Hamburger button hover background. Default: `var(--asc-bg-primary_hover)`
 * @cssprop --asc-navbar-actions-gap               - Gap between slotted action items (desktop). Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-navbar-actions-mobile-gap        - Gap between slotted action items (mobile). Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-navbar-floating-bar-padding-top  - Top padding of the bar in `floating-2-col`; creates the float offset. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-navbar-wrapper-border-color      - Border colour of the floating pill (`floating-2-col`). Default: `var(--asc-border-secondary)`
 * @cssprop --asc-navbar-wrapper-radius            - Border radius of the floating pill (`floating-2-col`). Default: `var(--asc-radius-lg)`
 * @cssprop --asc-navbar-wrapper-shadow            - Box shadow of the floating pill (`floating-2-col`). Default: `0 1px 2px 0 var(--asc-shadow-xs)`
 * @cssprop --asc-navbar-wrapper-padding-y         - Vertical padding inside the floating pill. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-navbar-wrapper-padding-start     - Inline-start padding inside the floating pill. Default: `var(--asc-spacing-xl)`
 * @cssprop --asc-navbar-wrapper-padding-end       - Inline-end padding inside the floating pill. Default: `var(--asc-spacing-lg)`
 */
@customElement('asc-navbar')
export class AscNavbar extends LitElement {
  static override styles = css`
    :host {
      container-type: inline-size;
      container-name: asc-navbar;
      display: block;
      position: relative;
    }

    /* CSS custom properties set on :host inherit through the normal DOM tree
       into any light-DOM children including asc-button elements.
       This is the only reliable cross-shadow mechanism for overriding child
       component host styles. */
    :host([mobile]) {
      --asc-button-display: block;
    }

    /* ── Base header ──────────────────────────────────────────── */
    .base {
      position: relative;
    }

    /* ── Bar ──────────────────────────────────────────────────── */
    .bar {
      background-color: var(--asc-navbar-bg, var(--asc-bg-primary));
      height: var(--asc-navbar-height-desktop, 80px); /* confirmed 3-col desktop */
      position: relative;
      z-index: 10;
    }

    :host([type='floating-2-col']) .bar {
      height: var(--asc-navbar-height-desktop-floating, 76px); /* confirmed floating-2-col desktop */
      /* Float the pill away from the top of the bar */
      padding-top: var(--asc-navbar-floating-bar-padding-top, var(--asc-spacing-lg));
    }

    /* ── Container ────────────────────────────────────────────── */
    .container {
      display: flex;
      align-items: center;
      gap: var(--asc-navbar-logo-nav-gap, var(--asc-spacing-2xl)); /* 20px per Figma: logo → nav-items */
      max-width: var(--asc-navbar-max-width, 1280px);
      height: 100%;
      margin-inline: auto;
      padding-inline: var(--asc-navbar-padding-x, var(--asc-container-padding-desktop));
    }

    /*
     * floating-2-col: .container becomes the floating pill.
     * The outer padding-inline on .container already provides viewport margins;
     * the pill gets its own tighter padding and visual treatment.
     */
    :host([type='floating-2-col']) .container {
      height: auto;
      border: 1px solid var(--asc-navbar-wrapper-border-color, var(--asc-border-secondary));
      border-radius: var(--asc-navbar-wrapper-radius, var(--asc-radius-lg));
      box-shadow: var(--asc-navbar-wrapper-shadow, 0 1px 2px 0 var(--asc-shadow-xs));
      padding-block: var(--asc-navbar-wrapper-padding-y, var(--asc-spacing-lg));
      padding-inline-start: var(--asc-navbar-wrapper-padding-start, var(--asc-spacing-xl));
      padding-inline-end: var(--asc-navbar-wrapper-padding-end, var(--asc-spacing-lg));
      background-color: var(--asc-bg-primary);
    }

    /* ── Logo area ────────────────────────────────────────────── */
    .logo-area {
      flex-shrink: 0;
      display: flex;
      align-items: center;
    }

    /* ── Nav area (desktop: inline, mobile: panel below bar) ──── */
    .nav-area {
      display: flex;
      align-items: center;
      flex: 1;
    }

    /* Inner wrapper — flex row on desktop, overridden to column on mobile */
    .nav-area-inner {
      display: flex;
      align-items: center;
      flex: 1;
      min-width: 0;
    }

    /* ── Nav items ────────────────────────────────────────────── */
    .nav-items {
      display: flex;
      align-items: center;
      gap: var(--asc-navbar-nav-items-gap, var(--asc-spacing-xxs)); /* 2px per Figma */
    }

    /* ── Actions area ─────────────────────────────────────────── */
    .actions-area {
      margin-inline-start: auto; /* push to far right inside the inner row */
      flex-shrink: 0;
      display: flex;
      align-items: center;
      gap: var(--asc-navbar-actions-gap, var(--asc-spacing-lg));
    }

    /* ── Hamburger (hidden on desktop) ────────────────────────── */
    .hamburger {
      display: none;
      align-items: center;
      justify-content: center;
      padding: var(--asc-navbar-hamburger-padding, var(--asc-spacing-md));
      border: none;
      border-radius: var(--asc-navbar-hamburger-radius, var(--asc-radius-md));
      background: transparent;
      cursor: pointer;
      margin-inline-start: auto;
      flex-shrink: 0;
    }

    .hamburger:hover {
      background-color: var(--asc-navbar-hamburger-bg-hover, var(--asc-bg-primary_hover));
    }

    .hamburger:focus-visible {
      outline: 2px solid var(--asc-focus-ring);
      outline-offset: 2px;
    }

    .hamburger-icon {
      display: block;
      /* Each icon uses its own intrinsic width/height from the SVG attributes */
    }

    /* ── Backdrop ─────────────────────────────────────────────── */
    .backdrop {
      display: none;
      position: fixed;
      inset: 0;
      background-color: var(--asc-navbar-backdrop-color, var(--asc-alpha-black-50));
      z-index: 9;
    }

    .backdrop:not([hidden]) {
      display: block;
    }

    /* ── Mobile layout: :host([mobile]) ─────────────────────────
     * The [mobile] attribute is toggled by NavbarController whenever
     * the element's offsetWidth drops below 1024 px. Using an attribute
     * selector is 100% reliable — no container query needed.
     * ─────────────────────────────────────────────────────────── */
    :host([mobile]) .bar {
      height: var(--asc-navbar-height-mobile, 72px);
    }

    :host([mobile][type='floating-2-col']) .bar {
      height: var(--asc-navbar-height-mobile-floating, 64px);
    }

    :host([mobile]) .container {
      padding-inline-start: var(--asc-navbar-mobile-padding-x, var(--asc-container-padding-mobile));
      padding-inline-end: var(--asc-navbar-mobile-padding-end, var(--asc-spacing-lg));
    }

    /* Reset floating pill on mobile — plain bar, no border/radius/shadow */
    :host([mobile][type='floating-2-col']) .bar {
      padding-top: 0;
    }

    :host([mobile][type='floating-2-col']) .container {
      height: 100%;
      border: none;
      border-radius: 0;
      box-shadow: none;
      padding-block: 0;
      background-color: transparent;
    }

    /* Nav area: move below bar as collapsible panel */
    :host([mobile]) .nav-area {
      position: absolute;
      inset-inline: 0;
      top: 100%;
      flex-direction: column;
      align-items: stretch;
      gap: 0;
      background-color: var(--asc-navbar-panel-bg, var(--asc-bg-primary));
      box-shadow: var(
        --asc-navbar-panel-shadow,
        0 12px 16px -4px var(--asc-shadow-lg_01),
        0 4px 6px -2px var(--asc-shadow-lg_02),
        0 2px 2px -1px var(--asc-shadow-lg_03)
      );
      display: grid;
      grid-template-rows: 0fr;
      overflow: hidden;
      transition: grid-template-rows var(--asc-navbar-panel-transition, 300ms) ease-out;
      z-index: 10;
    }

    :host([mobile][open]) .nav-area {
      grid-template-rows: 1fr;
    }

    :host([mobile]) .nav-area-inner {
      overflow: hidden;
      display: flex;
      flex-direction: column;
      align-items: stretch;
    }

    :host([mobile]) .nav-items {
      width: 100%;
      flex-direction: column;
      align-items: stretch;
      padding-block: var(--asc-navbar-panel-nav-padding-y, var(--asc-spacing-2xl));
      gap: var(--asc-navbar-panel-nav-gap, var(--asc-spacing-xxs));
      border-bottom: 1px solid var(--asc-navbar-panel-border-color, var(--asc-border-secondary));
    }

    :host([mobile]) .actions-area {
      margin-inline-start: 0;
      flex-direction: column-reverse; /* Figma: Sign up (primary CTA) first on mobile */
      align-items: stretch;
      gap: var(--asc-navbar-actions-mobile-gap, var(--asc-spacing-lg));
      padding-block: var(--asc-navbar-panel-actions-padding-y, var(--asc-spacing-3xl));
      padding-inline: var(--asc-navbar-panel-actions-padding-x, var(--asc-container-padding-mobile));
    }

    /* ::slotted width applies because asc-button has no explicit width on :host.
       Combined with --asc-button-display: block inherited from :host([mobile]),
       this gives buttons full width without relying solely on JS. */
    :host([mobile]) .actions-area ::slotted(*) {
      width: 100%;
    }

    /* Show hamburger on mobile */
    :host([mobile]) .hamburger {
      display: flex;
    }
  `;

  private readonly _controller = new NavbarController(this);
  private readonly _uid = generateUid();


  /** Layout variant. `3-col` = 80 px desktop bar; `floating-2-col` = 76 px desktop bar. */
  @property({ type: String, reflect: true }) type: NavbarType = '3-col';

  /** Whether the mobile navigation panel is open. */
  @property({ type: Boolean, reflect: true }) open = false;

  private _onNavSlotChange(): void {
    this._propagateToNavItems();
  }

  private _onActionsSlotChange(): void {
    this._syncActionStyles();
  }

  override updated(_changed: PropertyValues): void {
    // Always propagate — fired after NavbarController.requestUpdate() (ResizeObserver)
    // and after any property change. This is the single source of truth for child state.
    this._propagateToNavItems();
    this._syncActionStyles();
  }

  private _propagateToNavItems(): void {
    const isMobile = this._controller.isMobile;
    this.querySelectorAll('asc-nav-item').forEach((item) => {
      const el = item as unknown as NavItemContract;
      el.type = this.type;
      el.inNavbar = true;
      el.navbarHost = this;
      // Propagate mobile state directly — more reliable than each nav-item
      // reading its own offsetWidth at connectedCallback time.
      el.mobile = isMobile;
    });
  }

  /**
   * Sets inline display/width on slotted action elements when mobile.
   * Inline styles are the only reliable way to cross shadow DOM boundaries
   * — they beat all shadow-internal :host styles regardless of cascade order.
   */
  private _syncActionStyles(): void {
    const isMobile = this._controller.isMobile;
    this.querySelectorAll<HTMLElement>('[slot="actions"]').forEach((el) => {
      if (isMobile) {
        el.setAttribute('data-navbar-mobile', '');
        el.style.display = 'block';
        el.style.width = '100%';
      } else {
        el.removeAttribute('data-navbar-mobile');
        el.style.display = '';
        el.style.width = '';
      }
    });
  }

  private _handleHamburgerClick(): void {
    const next = !this.open;
    this.open = next;
    dispatch(this, next ? 'asc-open' : 'asc-close');
  }

  private _handleBackdropClick(): void {
    this.open = false;
    dispatch(this, 'asc-close');
  }

  override render() {
    const navPanelId = `nav-panel-${this._uid}`;
    const hamburgerId = `hamburger-${this._uid}`;
    const mobileInert = !this.open && this._controller.isMobile;

    return html`
      <header part="base" class="base">
        <div part="bar" class="bar">
          <div part="container" class="container">

            <div part="logo-area" class="logo-area">
              <slot name="logo"></slot>
            </div>

            <nav
              part="nav-area"
              class="nav-area"
              id="${navPanelId}"
              aria-label="Site navigation"
              ?inert="${mobileInert}"
            >
              <div class="nav-area-inner">
                <div part="nav-items" class="nav-items">
                  <slot @slotchange="${this._onNavSlotChange}"></slot>
                </div>
                <div part="actions-area" class="actions-area">
                  <slot name="actions" @slotchange="${this._onActionsSlotChange}"></slot>
                </div>
              </div>
            </nav>

            <button
              part="hamburger"
              class="hamburger"
              type="button"
              id="${hamburgerId}"
              aria-label="${this.open ? 'Close navigation menu' : 'Open navigation menu'}"
              aria-expanded="${String(this.open)}"
              aria-controls="${navPanelId}"
              @click="${this._handleHamburgerClick}"
            >
              ${this.open
                ? html`
                    <svg class="hamburger-icon" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                      <path d="M15.75 0C15.95 0 16.1393 0.0777274 16.2803 0.219727C16.4223 0.360727 16.5 0.55 16.5 0.75C16.5 0.95 16.4223 1.13927 16.2803 1.28027L9.31055 8.25L16.2812 15.2197C16.5732 15.5117 16.5732 15.9882 16.2812 16.2812C16.1392 16.4223 15.95 16.5 15.75 16.5C15.55 16.5 15.3607 16.4223 15.2197 16.2803L8.25 9.31055L1.28027 16.2812C1.13827 16.4233 0.95 16.501 0.75 16.501C0.55 16.501 0.360727 16.4233 0.219727 16.2812C-0.0722616 15.9892 -0.0722695 15.5127 0.219727 15.2197L7.19043 8.25L0.219727 1.28027C0.0777273 1.13927 0 0.95 0 0.75C0 0.55 0.0777272 0.360726 0.219727 0.219727C0.360726 0.0777272 0.55 0 0.75 0C0.95 0 1.13927 0.0777273 1.28027 0.219727L8.25 7.19043L15.2197 0.219727C15.3617 0.0777268 15.55 0 15.75 0Z" fill="currentColor"/>
                    </svg>
                  `
                : html`
                    <svg class="hamburger-icon" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="21" height="14" viewBox="0 0 21 14" fill="none">
                      <path d="M20.25 12C20.664 12 21 12.336 21 12.75C21 13.164 20.664 13.5 20.25 13.5H0.75C0.336 13.5 0 13.164 0 12.75C0 12.336 0.336 12 0.75 12H20.25ZM20.25 6C20.664 6 21 6.336 21 6.75C21 7.164 20.664 7.5 20.25 7.5H0.75C0.336 7.5 0 7.164 0 6.75C0 6.336 0.336 6 0.75 6H20.25ZM20.25 0C20.664 0 21 0.336 21 0.75C21 1.164 20.664 1.5 20.25 1.5H0.75C0.336 1.5 0 1.164 0 0.75C0 0.336 0.336 0 0.75 0H20.25Z" fill="currentColor"/>
                    </svg>
                  `}
            </button>

          </div>
        </div>

        <div
          part="backdrop"
          class="backdrop"
          ?hidden="${!this.open || !this._controller.isMobile}"
          aria-hidden="true"
          @click="${this._handleBackdropClick}"
        >
        </div>
      </header>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-navbar': AscNavbar;
  }
}
